import { Route, Router, type RouteSectionProps, useLocation } from '@solidjs/router';
import { createEffect, createSignal, type JSX, Match, Switch } from 'solid-js';
import { AppErrorBoundary } from './components/AppErrorBoundary';
import { AppShell } from './components/AppShell';
import { ConsentGate, type ConsentReread } from './components/ConsentGate';
import { CrossBorderGate } from './components/CrossBorderGate';
import { ErrorState } from './components/ErrorState';
import { OrgScope } from './components/OrgScope';
import { RegionGate } from './components/RegionGate';
import { UpdatePrompt } from './components/UpdatePrompt';
import { WaitlistGate } from './components/WaitlistGate';
import { t } from './i18n';
import { type AppGate, resolveAppGate } from './lib/app-gate';
import { ORG_BASE_PATH, ORG_ROUTE_TABLE, TOP_ROUTE_TABLE } from './lib/routes';
import {
  type Me,
  needsConsent,
  needsCrossBorderConsent,
  needsRegion,
  SESSION_TOKEN,
  useSessionMe,
} from './lib/session';
import { sessionExpired } from './lib/session-expiry';
import { Login } from './routes/Login';

// Top-level router + auth gate. `useSessionMe()` probes the session and
// `resolveAppGate` (lib/app-gate.ts) turns that — plus the global 401 latch —
// into exactly one of eight views: checking / login / waitlisted / consent /
// region / cross_border / app / outage.
// In `vite dev` the seeded PAT authenticates; in prod the httpOnly session
// cookie does (mocks short-circuit to signed-in). `root={GateShell}` wraps every
// route in the header/nav/footer chrome AND reports the Router's live pathname
// back to the gate, which is what makes the waitlist's `/invite` exemption
// reactive rather than a load-time snapshot.
export function App(props: { token?: string | null }): JSX.Element {
  // `token` mirrors `useMe`'s own parameter — it defaults to the session
  // credential and exists so the gate can be driven in a test without Vite env.
  // Resolved ONCE and handed on: a gate screen that reads the session must ask
  // with the same credential the gate itself probed with.
  const token = props.token === undefined ? SESSION_TOKEN : props.token;
  // The SESSION's identity, never the URL org's: an org the API refuses (403)
  // is reported inside the shell by <OrgScope/>, not as an app-wide outage.
  const me = useSessionMe(token);
  // The gate's `path`. Seeded from the location at load — which is how a
  // waitlisted session reaches `/invite` at all — and then kept current by
  // `<GateShell/>` below, which sits INSIDE the Router and can therefore see the
  // pushState navigations `App` cannot.
  const [path, setPath] = createSignal(window.location.pathname);
  /**
   * The Router root: the app chrome, plus the one line that reports the live
   * pathname back out to {@link resolveAppGate}.
   *
   * It is here rather than in `<AppShell/>` because the shell is a presentation
   * component with three other callers, and because this is the ONLY place the
   * two facts meet — the Router is mounted (so `useLocation` resolves) and the
   * gate's signal is in scope. When the gate flips to `waitlisted` this whole
   * subtree unmounts, the effect disposes, and the last reported path is the one
   * the wall was raised over; nothing can navigate from the wall, so it stays
   * true until the next full load re-seeds the signal.
   */
  const GateShell = (shellProps: RouteSectionProps): JSX.Element => {
    const location = useLocation();
    createEffect(() => setPath(location.pathname));
    return <AppShell token={token}>{shellProps.children}</AppShell>;
  };
  // A 401 ANYWHERE (not just on `/v1/me`) means the credential is gone: the
  // latch outranks the account still sitting in the query cache, so an expiry
  // mid-session lands on <Login/> instead of leaving every panel to 401 alone.
  const gate = (): AppGate =>
    resolveAppGate({
      expired: sessionExpired(),
      loading: me.isLoading,
      me: me.data,
      error: me.error,
      // TRACKED, not read once: `<GateShell/>` feeds the Router's own location
      // into this signal, so leaving `/invite` re-runs the gate. See
      // `AppGateInput.path` for the two ways a waitlisted session ends up there
      // and stays waitlisted.
      path: path(),
    });
  // A wall's re-read: re-ask `/v1/me` and answer whether the wall `still` stands.
  // A failed refetch keeps the old `me`, so the wall stays up (19-17).
  const reread = async (still: (m: Me) => boolean): Promise<ConsentReread> => {
    const next = await me.refetch();
    return next.data !== undefined && !still(next.data) ? 'cleared' : 'still_walled';
  };
  return (
    <AppErrorBoundary>
      {/* App-wide, auth-independent: a waiting SW build offers a reload here
          (prod + SW only; inert otherwise). See components/UpdatePrompt. */}
      <UpdatePrompt />
      <Switch fallback={<Login />}>
        <Match when={gate() === 'checking'}>
          <main class="login">
            <p class="login__checking">{t('login.checking')}</p>
          </main>
        </Match>
        {/* Closed-beta waitlist wall: a signed-in human the server has NOT
            approved. `sessionScopes` has already capped the session to
            `read:account`, so the shell it used to land in was a console whose
            every panel 403'd on its own. It OUTRANKS consent because the
            server's ladder does, and `/invite` is exempt — redeeming an
            invitation is the act that lifts the wall, and it falls through to
            the app Router rather than mounting a second one (lib/app-gate.ts). */}
        <Match when={gate() === 'waitlisted'}>
          <WaitlistGate token={token} />
        </Match>
        {/* ToS/consent gate (#554): a signed-in user who hasn't accepted the
            current terms is walled here — no route renders until they do. The
            wall is the UX half; the API caps a stale-ToS session to
            `read:account` itself (`sessionScopes`, api `middleware/scopes.ts`).
            The re-read's answer goes back
            to the wall: a failed refetch keeps the old `me`, so the wall stays
            up and must hand its button back (19-17). */}
        <Match when={gate() === 'consent'}>
          <ConsentGate
            version={me.data?.tos?.current_version ?? ''}
            token={token}
            onAccepted={() => reread(needsConsent)}
          />
        </Match>
        {/* The region question, then the separate cross-border consent (#5881),
            each its own screen and never bundled with the ToS above. The API
            caps a CN session missing that consent to `read:account` itself. */}
        <Match when={gate() === 'region'}>
          <RegionGate token={token} onSaved={() => reread(needsRegion)} />
        </Match>
        <Match when={gate() === 'cross_border'}>
          <CrossBorderGate
            version={me.data?.cross_border_consent?.current_version ?? ''}
            token={token}
            onAccepted={() => reread(needsCrossBorderConsent)}
          />
        </Match>
        <Match when={gate() === 'app'}>
          <Router root={GateShell}>
            {/* URL-based org scoping (#460): account-scoped screens nest under
                /org/:org; <OrgScope/> feeds the segment into query keys + the
                X-Dz-Account header. Top-level: /admin + the legacy catch-all. */}
            <Route
              path={ORG_BASE_PATH}
              component={(p) => <OrgScope token={token}>{p.children}</OrgScope>}
            >
              {ORG_ROUTE_TABLE.map((r) => (
                <Route path={r.path} component={r.component} />
              ))}
            </Route>
            {TOP_ROUTE_TABLE.map((r) => (
              <Route path={r.path} component={r.component} />
            ))}
          </Router>
        </Match>
        <Match when={gate() === 'outage'}>
          {/* Outside the Router, so the Retry IS the exit: it re-asks the
              session probe, and a healthy answer mounts the app. */}
          <main class="login">
            <ErrorState error={me.error} onRetry={() => void me.refetch()} />
          </main>
        </Match>
      </Switch>
    </AppErrorBoundary>
  );
}
